import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Repeat, Upload, Send, Euro, X } from "lucide-react";
import { toast } from "sonner";
import { bartersAPI, uploadImage, resolveImg } from "../lib/api";

export default function BarterModal({ open, onClose, listing }) {
  const [itemTitle, setItemTitle] = useState("");
  const [itemValue, setItemValue] = useState("");
  const [itemDesc, setItemDesc] = useState("");
  const [photos, setPhotos] = useState([]);
  const [addCash, setAddCash] = useState(false);
  const [cashAmount, setCashAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - photos.length);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const url = await uploadImage(f);
        urls.push(url);
      }
      setPhotos([...photos, ...urls].slice(0, 3));
    } catch (err) {
      toast.error("Errore upload foto");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (i) => setPhotos(photos.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!itemTitle.trim()) {
      toast.error("Inserisci cosa offri in baratto");
      return;
    }
    setLoading(true);
    try {
      await bartersAPI.create(listing.id, {
        item_title: itemTitle,
        item_value: Number(itemValue) || 0,
        item_description: itemDesc,
        photos,
        add_cash: addCash,
        cash_amount: Number(cashAmount) || 0,
      });
      const parts = [`"${itemTitle}"`];
      if (addCash && cashAmount) parts.push(`+ €${cashAmount}`);
      toast.success(`Proposta di baratto inviata!`, { description: parts.join(" ") });
      setItemTitle(""); setItemValue(""); setItemDesc(""); setPhotos([]); setAddCash(false); setCashAmount("");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio baratto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Repeat className="w-5 h-5 text-emerald-600" />Proponi un baratto</DialogTitle>
          <DialogDescription>Scambia il tuo oggetto con: <span className="font-bold text-slate-900">{listing.title}</span> (€ {Number(listing.price).toLocaleString("it-IT")})</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold text-slate-700 block mb-2">Cosa offri in cambio?</label>
            <Input value={itemTitle} onChange={(e) => setItemTitle(e.target.value)} placeholder="Es: PlayStation 5 Slim 1TB" className="h-11" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-2">Valore stimato</label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type="number" value={itemValue} onChange={(e) => setItemValue(e.target.value)} placeholder="0" className="pl-9 h-11" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-2">Categoria</label>
              <Input placeholder="Es: Elettronica" className="h-11" />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-700 block mb-2">Descrizione</label>
            <Textarea value={itemDesc} onChange={(e) => setItemDesc(e.target.value)} placeholder="Descrivi condizione, dettagli, accessori inclusi..." rows={3} />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-700 block mb-2">Foto del tuo oggetto (max 3)</label>
            <div className="grid grid-cols-3 gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200">
                  <img src={resolveImg(p)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center">
                    <X className="w-3 h-3"/>
                  </button>
                </div>
              ))}
              {photos.length < 3 && (
                <label className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 transition-colors">
                  <Upload className="w-5 h-5 text-slate-400 mb-1"/>
                  <span className="text-[10px] text-slate-500">{uploading ? "..." : "Carica"}</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} disabled={uploading} />
                </label>
              )}
            </div>
          </div>

          <div className="border-2 border-emerald-200 bg-emerald-50 rounded-xl p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={addCash} onChange={(e) => setAddCash(e.target.checked)} className="mt-1 accent-emerald-600" />
              <div className="flex-1">
                <div className="text-sm font-bold text-emerald-900 flex items-center gap-1.5">
                  <Euro className="w-4 h-4" /> Aggiungi anche denaro
                </div>
                <div className="text-xs text-emerald-700">Baratto + conguaglio in € se il tuo oggetto vale meno</div>
              </div>
            </label>
            {addCash && (
              <div className="mt-3">
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="number" value={cashAmount} onChange={(e) => setCashAmount(e.target.value)} placeholder="Aggiungi conguaglio (es. 50)" className="pl-9 h-11 bg-white" />
                </div>
                <div className="text-xs text-emerald-700 mt-2">
                  Totale proposta: <span className="font-bold">"{itemTitle || "oggetto"}"</span> + <span className="font-bold">€{cashAmount || "0"}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Annulla</Button>
          <Button onClick={submit} disabled={loading} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
            <Send className="w-4 h-4 mr-2"/>{loading ? "Invio..." : "Invia proposta"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
